import { useDroppable, useDraggable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader } from 'reactstrap';
import { AssignmentCard } from './AssignmentCard';
import type { PoolCard } from '../../api/assignments';

export interface PoolColumnProps {
  cards: PoolCard[];
  onCardClick: (cardId: string, e: React.MouseEvent) => void;
  selectedIds: Set<string>;
  onSplit: (pickListId: number) => void;
}

export function poolCardId(card: PoolCard): string {
  return card.kind === 'whole_list'
    ? `pool-pl-${card.pick_list.id}`
    : `pool-shard-${card.mission_id}`;
}

export function PoolColumn({ cards, onCardClick, selectedIds, onSplit }: PoolColumnProps) {
  const { t } = useTranslation('assignment');
  const { setNodeRef, isOver } = useDroppable({ id: 'pool' });

  // Pool vuoto → striscia compatta che recupera spazio per gli operatori, ma
  // resta una drop-zone (stesso droppable id:'pool'): ci si può ancora
  // ri-trascinare una missione "Da assegnare".
  if (cards.length === 0) {
    return (
      <Card className="mb-0 vp-pool-collapsed" title={t('pool.header')}>
        <div
          ref={setNodeRef}
          role="region"
          aria-label={t('pool.regionAria', { count: 0 })}
          className={`vp-pool-collapsed__body ${isOver ? 'bg-primary-subtle' : ''}`}
        >
          <i className="bi bi-inboxes text-muted" />
          <span className="vp-pool-collapsed__label">{t('pool.header')}</span>
          <span className="badge rounded-pill bg-light text-muted fw-medium">0</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="mb-0">
      <CardHeader className="py-2 px-2 d-flex align-items-center bg-body">
        <span className="vp-board-accent flex-shrink-0" />
        <i className="bi bi-inboxes text-muted ms-2 me-1" />
        <span className="fw-semibold flex-grow-1">{t('pool.header')}</span>
        <span className="badge rounded-pill bg-light text-muted fw-medium">
          {cards.length}
        </span>
      </CardHeader>
      {/* dnd-kit droppable: ref on a plain div to avoid reactstrap forwardRef ambiguity. */}
      <div
        ref={setNodeRef}
        role="region"
        aria-label={t('pool.regionAria', { count: cards.length })}
        className={`card-body p-2 ${isOver ? 'bg-primary-subtle' : ''}`}
        style={{ minHeight: 120 }}
      >
        {/* Griglia 2-per-riga delle pick-list da assegnare. */}
        <div className="vp-pool-grid">
          {cards.map((card) => (
            <DraggablePoolCard
              key={poolCardId(card)}
              card={card}
              isSelected={selectedIds.has(poolCardId(card))}
              onClick={(e) => onCardClick(poolCardId(card), e)}
              onSplit={onSplit}
            />
          ))}
        </div>
      </div>
    </Card>
  );
}

function DraggablePoolCard({ card, isSelected, onClick, onSplit }:
  { card: PoolCard; isSelected: boolean;
    onClick: (e: React.MouseEvent) => void; onSplit: (id: number) => void; }) {
  const id = poolCardId(card);
  // No transform on the source: the floating proxy is the <DragOverlay>. Moving
  // the source through the document made it slide UNDER the operator columns
  // (z-index trap). While dragging we just hide the source (overlay shows it).
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners}
         style={{ opacity: isDragging ? 0 : 1, cursor: 'grab', touchAction: 'none' }}>
      {card.kind === 'whole_list'
        ? <AssignmentCard kind="whole_list" location="pool" pickList={card.pick_list}
                          isSelected={isSelected} onClick={onClick} onSplit={onSplit} />
        : <AssignmentCard kind="self_claim_shard" location="pool" pickList={card.pick_list}
                          shardIndex={card.shard_index} shardTotal={card.shard_total}
                          qtyAssignedTotal={card.qty_assigned_total} taskCount={card.task_count}
                          isSelected={isSelected} onClick={onClick} />}
    </div>
  );
}
